import React, { useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination // Import TablePagination
} from '@mui/material';
import { showGlobalNotification } from '../context/NotificationContext.js';
import { useTheme } from '@mui/material/styles';
import { FilterList as FilterListIcon } from '@mui/icons-material'; // Import FilterListIcon
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WarningIcon from '@mui/icons-material/Warning';
import ReplayIcon from '@mui/icons-material/Replay'; // Import Revert icon
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext'; // Corrected import for user context
import { usePermissions } from '../context/PermissionContext'; // Corrected import for permissions context
import SideMenu from '../components/SideMenu'; // Import SideMenu
import JobDetails from '../components/JobDetails';
import { usePageTour } from '../tours/usePageTour';
import JobDialog from '../components/JobDialog';
import EditInvoiceDialog from '../components/EditInvoiceDialog'; // Import the new EditInvoiceDialog component

// Custom tab panel component for each tab
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payment-tabpanel-${index}`}
      aria-labelledby={`payment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Payment Records tab: weekly ACH payout history with date search and CSV export
function PayoutRecordsPanel({ user }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [includeCurrentWeek, setIncludeCurrentWeek] = useState(true);
  const [searched, setSearched] = useState(false);

  const isOrg1Admin = user?.organizationLevel === 1 && (
    user?.secondaryRoles?.admin === true ||
    (Array.isArray(user?.secondaryRoles) && user.secondaryRoles.includes('admin')) ||
    ['OW', 'sOW', 'OWp'].includes(user?.primaryRole)
  );

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get('/api/payments/records', { headers: authHeader(), params });
      setRecords(res.data || []);
      setSearched(true);
    } catch (err) {
      showGlobalNotification(err.response?.data?.message || 'Failed to load payment records.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadRecordCsv = (record) => {
    const dateStr = new Date(record.periodEnd).toISOString().slice(0, 10);
    downloadCsv(record.csv || '', `transaction-journal-${record.payeeName || record.vendorNumber}-${dateStr}.csv`);
  };

  // Combined export of the current search results (for Org 1 records)
  const exportAllCsv = () => {
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = ['Payment Date,Payee,Vendor,Jobs,Job POs,Total,Emailed To,Emailed At'];
    records.forEach(r => {
      lines.push([
        new Date(r.periodEnd).toISOString().slice(0, 10),
        esc(r.payeeName),
        esc(r.vendorNumber || ''),
        r.jobs?.length || 0,
        esc((r.jobs || []).map(j => j.po).join(' ')),
        Number(r.totalAmount).toFixed(2),
        esc(r.emailedTo || ''),
        r.emailedAt ? new Date(r.emailedAt).toISOString() : ''
      ].join(','));
    });
    downloadCsv(lines.join('\n'), `payment-records-${startDate || 'all'}-to-${endDate || 'all'}.csv`);
  };

  const runWeeklyPayout = async () => {
    setRunning(true);
    try {
      const res = await axios.post('/api/payments/records/run-weekly', { includeCurrentWeek }, { headers: authHeader() });
      showGlobalNotification(res.data.message || 'Payout run complete.', 'success');
      fetchRecords();
    } catch (err) {
      showGlobalNotification(err.response?.data?.message || 'Failed to run payout.', 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Payment Records</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weekly ACH payouts. Each record lists the jobs covered by that payment; download the
        transaction journal CSV for your records.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField label="From (payment date)" type="date" size="small" value={startDate}
          onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="To (payment date)" type="date" size="small" value={endDate}
          onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" onClick={fetchRecords} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </Button>
        {records.length > 0 && (
          <Button variant="outlined" onClick={exportAllCsv}>Export All to CSV</Button>
        )}
        {isOrg1Admin && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Include jobs completed this week (instead of waiting for Sunday)">
              <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={includeCurrentWeek} onChange={(e) => setIncludeCurrentWeek(e.target.checked)} />
                &nbsp;Include current week
              </label>
            </Tooltip>
            <Button variant="outlined" color="warning" onClick={runWeeklyPayout} disabled={running}>
              {running ? 'Running...' : 'Run Weekly Payout Now'}
            </Button>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : records.length === 0 ? (
        <Alert severity="info">
          {searched ? 'No payment records found for the selected dates.' : 'Choose a date range (or leave blank for all) and press Search.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Payment Date</TableCell>
                <TableCell>Payee</TableCell>
                <TableCell>Vendor ID</TableCell>
                <TableCell>Jobs In Payment</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Emailed</TableCell>
                <TableCell align="right">Journal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id || r._id} hover>
                  <TableCell>{new Date(r.periodEnd).toLocaleDateString()}</TableCell>
                  <TableCell>{r.payeeName}</TableCell>
                  <TableCell>{r.vendorNumber || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={(r.jobs || []).map(j => `${j.po}: $${Number(j.amount).toFixed(2)}`).join(' | ')}>
                      <span>{(r.jobs || []).length} job{(r.jobs || []).length === 1 ? '' : 's'} ({(r.jobs || []).slice(0, 4).map(j => j.po).join(', ')}{(r.jobs || []).length > 4 ? '…' : ''})</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">${Number(r.totalAmount).toFixed(2)}</TableCell>
                  <TableCell>
                    {r.emailedAt
                      ? <Chip label={`Sent ${new Date(r.emailedAt).toLocaleDateString()}`} color="success" size="small" />
                      : <Tooltip title={r.emailError || 'Not emailed'}><Chip label="Not emailed" color="warning" size="small" /></Tooltip>}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => downloadRecordCsv(r)} disabled={!r.csv}>CSV</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// Main Payments component
const Payments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentUser: user } = useUser(); // Use useUser hook and alias currentUser to user
  const { hasPermission } = usePermissions(); // Use usePermissions hook and get hasPermission method
  usePageTour('payments', !!user); // first-visit tour of this page

  // Vehicle data state
  const [vehicleData, setVehicleData] = useState({
    makes: [],
    models: {},
    years: [],
    colors: []
  });

  // Fetch vehicle data (fleet vehicles) on mount
  useEffect(() => {
    async function fetchVehicleData() {
      // Try localStorage first
      const cachedVehicles = localStorage.getItem('fleetVehicles');
      let vehiclesArray = [];
      if (cachedVehicles) {
        try {
          const parsed = JSON.parse(cachedVehicles);
          vehiclesArray = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          vehiclesArray = [];
        }
      } else {
        // Fetch from API
        try {
          const token = localStorage.getItem('token');
          const headers = { 'Content-Type': 'application/json' };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
            headers: headers
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data && data.vehicles && Array.isArray(data.vehicles)) {
            vehiclesArray = data.vehicles;
            localStorage.setItem('fleetVehicles', JSON.stringify(data.vehicles));
          } else {
            vehiclesArray = [];
          }
        } catch (error) {
          console.error('Error fetching vehicle data from API:', error);
          vehiclesArray = [];
        }
      }

      // Ensure vehiclesArray is always an array before processing
      if (!Array.isArray(vehiclesArray)) {
        vehiclesArray = [];
      }

      // Transform vehiclesArray into the required format
      const makesSet = new Set();
      const modelsMap = {};
      const yearsSet = new Set();
      const colorsSet = new Set();

      vehiclesArray.forEach(vehicle => {
        if (vehicle.make) {
          makesSet.add(vehicle.make);
          if (!modelsMap[vehicle.make]) {
            modelsMap[vehicle.make] = new Set();
          }
          if (vehicle.model) {
            modelsMap[vehicle.make].add(vehicle.model);
          }
        }
        if (vehicle.year) yearsSet.add(vehicle.year);
        if (vehicle.color) colorsSet.add(vehicle.color);
      });

      // Convert sets to arrays and sort
      const makes = Array.from(makesSet).sort();
      const models = {};
      Object.keys(modelsMap).forEach(make => {
        models[make] = Array.from(modelsMap[make]).sort();
      });
      const years = Array.from(yearsSet).sort((a, b) => b - a); // Descending
      const colors = Array.from(colorsSet).sort();

      setVehicleData({
        makes,
        models,
        years,
        colors
      });
    }
    fetchVehicleData();
  }, []);

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Filters state
  const [filters, setFilters] = useState({
    facilityId: '',
    city: '',
    state: '',
    zip: '',
    service: '',
    customerName: '',
    customerPhone: '',
    poNumber: ''
  });
  const [facilities, setFacilities] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Jobs data state
  const [newJobs, setNewJobs] = useState([]);
  const [submittedJobs, setSubmittedJobs] = useState([]);
  const [finishedJobs, setFinishedJobs] = useState([]);
  const [attentionJobs, setAttentionJobs] = useState([]);

  // Quick search across the visible invoice lists (PO, driver, account, customer, service, city)
  const [paymentSearch, setPaymentSearch] = useState('');
  const filterJobsBySearch = (jobs) => {
    const term = paymentSearch.trim().toLowerCase();
    if (!term) return jobs;
    return jobs.filter((job) => {
      const haystack = [
        job.po,
        job.driver,
        job.account,
        job.customerName,
        job.service,
        job.serviceLocation?.city,
        job.assignedSPCompanyName
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  };

  // Pagination state for each tab
  const [newPagination, setNewPagination] = useState({ page: 0, rowsPerPage: 10, totalCount: 0 });
  const [submittedPagination, setSubmittedPagination] = useState({ page: 0, rowsPerPage: 10, totalCount: 0 });
  const [finishedPagination, setFinishedPagination] = useState({ page: 0, rowsPerPage: 10, totalCount: 0 });
  const [attentionPagination, setAttentionPagination] = useState({ page: 0, rowsPerPage: 10, totalCount: 0 });
  
  // Invoice totals cache: { [jobId]: totalCost }
  const [invoiceTotals, setInvoiceTotals] = useState({});

  // Fetch invoice totals for visible jobs
  useEffect(() => {
    // Combine all jobs in all tabs
    const allJobs = [...newJobs, ...submittedJobs, ...finishedJobs, ...attentionJobs];
    const jobsToFetch = allJobs.filter(job => job && job._id && invoiceTotals[job._id] === undefined);

    if (jobsToFetch.length === 0) return;

    jobsToFetch.forEach(job => {
      axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${job._id}/invoice-details`)
        .then(res => {
          setInvoiceTotals(prev => ({
            ...prev,
            [job._id]: res.data?.invoice?.total ?? null
          }));
        })
        .catch(() => {
          setInvoiceTotals(prev => ({
            ...prev,
            [job._id]: null
          }));
        });
    });
    // eslint-disable-next-line
  }, [newJobs, submittedJobs, finishedJobs, attentionJobs]);

  // Loading states
  const [loading, setLoading] = useState({
    new: true,
    submitted: false,
    finished: false,
    attention: false
  });
  
  // Error states
  const [error, setError] = useState({
    new: null,
    submitted: null,
    finished: null,
    attention: null
  });
  
  // Dialog states
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    jobId: null,
    reason: ''
  });
  
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    jobId: null,
    amount: 0,
    payPeriodStart: null,
    payPeriodEnd: null,
    notes: ''
  });
  
  // Expanded job state
  const [expandedJob, setExpandedJob] = useState(null);

  // Edit job dialog state
  const [editJob, setEditJob] = useState(null);
  
  // Edit invoice dialog state
  const [editInvoice, setEditInvoice] = useState({
    open: false,
    jobId: null
  });
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    
    // Load data for the selected tab if it hasn't been loaded yet (without filters)
    switch (newValue) {
      case 0: // New tab
        // Always fetch when tab changes, respecting current pagination for that tab
        fetchNewJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPagination.page, newPagination.rowsPerPage);
        break;
      case 1: // Submitted tab
        fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), submittedPagination.page, submittedPagination.rowsPerPage);
        break;
      case 2: // Finished tab
        fetchFinishedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), finishedPagination.page, finishedPagination.rowsPerPage);
        break;
      case 3: // Attention tab
        fetchAttentionJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), attentionPagination.page, attentionPagination.rowsPerPage);
        break;
      default:
        break;
    }
  };

  // Handle search button click to apply filters
  const handleSearch = () => {
    // Apply filters to the current tab
    switch (currentTab) {
      case 0: // New tab
        setNewPagination(prev => ({ ...prev, page: 0 })); // Reset page on new search
        fetchNewJobs(true, 0, newPagination.rowsPerPage);
        break;
      case 1: // Submitted tab
        setSubmittedPagination(prev => ({ ...prev, page: 0 }));
        fetchSubmittedJobs(true, 0, submittedPagination.rowsPerPage);
        break;
      case 2: // Finished tab
        setFinishedPagination(prev => ({ ...prev, page: 0 }));
        fetchFinishedJobs(true, 0, finishedPagination.rowsPerPage);
        break;
      case 3: // Attention tab
        setAttentionPagination(prev => ({ ...prev, page: 0 }));
        fetchAttentionJobs(true, 0, attentionPagination.rowsPerPage);
        break;
      default:
        break;
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      facilityId: '',
      city: '',
      state: '',
      zip: '',
      service: '',
      customerName: '',
      customerPhone: '',
      poNumber: ''
    });
    // Reload current tab without filters
    switch (currentTab) {
      case 0: // New tab
        fetchNewJobs(false, newPagination.page, newPagination.rowsPerPage);
        break;
      case 1: // Submitted tab
        fetchSubmittedJobs(false, submittedPagination.page, submittedPagination.rowsPerPage);
        break;
      case 2: // Finished tab
        fetchFinishedJobs(false, finishedPagination.page, finishedPagination.rowsPerPage);
        break;
      case 3: // Attention tab
        fetchAttentionJobs(false, attentionPagination.page, attentionPagination.rowsPerPage);
        break;
      default:
        break;
    }
  };
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (e) {
      return 'Invalid Date';
    }
  };
  
  // Toggle filter visibility
  const toggleFilterVisibility = () => {
    setShowFilters(prev => !prev);
  };
  
  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };
  
  // Build query params from filters
  const buildFilterParams = (pageNum = 0, rowsPerPageNum = 10) => {
    const params = new URLSearchParams();
    params.append('page', pageNum + 1);
    params.append('limit', rowsPerPageNum);
    if (filters.facilityId) params.append('facilityId', filters.facilityId);
    if (filters.city) params.append('city', filters.city);
    if (filters.state) params.append('state', filters.state);
    if (filters.zip) params.append('zip', filters.zip);
    if (filters.service) params.append('service', filters.service);
    if (filters.customerName) params.append('customerName', filters.customerName);
    if (filters.customerPhone) params.append('customerPhone', filters.customerPhone);
    if (filters.poNumber) params.append('poNumber', filters.poNumber);
    return params.toString();
  };

  // Fetch functions for each tab
  const fetchNewJobs = async (applyFilters = false, pageNum = 0, rowsPerPageNum = 10) => {
    setLoading(prev => ({ ...prev, new: true }));
    setError(prev => ({ ...prev, new: null }));
    
    try {
      const queryString = buildFilterParams(pageNum, rowsPerPageNum);
      const url = `${process.env.REACT_APP_API_URL}/api/payments/unsubmitted?${queryString}`;
      
      const response = await axios.get(url);
      setNewJobs(response.data.jobs || []);
      setNewPagination(prev => ({ ...prev, totalCount: response.data.totalCount || 0 }));
    } catch (err) {
      console.error('Error fetching new jobs:', err);
      setError(prev => ({ ...prev, new: err.response?.data?.message || 'Failed to load jobs' }));
    } finally {
      setLoading(prev => ({ ...prev, new: false }));
    }
  };
  
  const fetchSubmittedJobs = async (applyFilters = false, pageNum = 0, rowsPerPageNum = 10) => {
    setLoading(prev => ({ ...prev, submitted: true }));
    setError(prev => ({ ...prev, submitted: null }));
    
    try {
      const queryString = buildFilterParams(pageNum, rowsPerPageNum);
      const url = `${process.env.REACT_APP_API_URL}/api/payments/submitted?${queryString}`;
      
      const response = await axios.get(url);
      setSubmittedJobs(response.data.jobs || []);
      setSubmittedPagination(prev => ({ ...prev, totalCount: response.data.totalCount || 0 }));
    } catch (err) {
      console.error('Error fetching submitted jobs:', err);
      setError(prev => ({ ...prev, submitted: err.response?.data?.message || 'Failed to load jobs' }));
    } finally {
      setLoading(prev => ({ ...prev, submitted: false }));
    }
  };
  
  const fetchFinishedJobs = async (applyFilters = false, pageNum = 0, rowsPerPageNum = 10) => {
    setLoading(prev => ({ ...prev, finished: true }));
    setError(prev => ({ ...prev, finished: null }));
    
    try {
      const queryString = buildFilterParams(pageNum, rowsPerPageNum);
      const url = `${process.env.REACT_APP_API_URL}/api/payments/finished?${queryString}`;
      
      const response = await axios.get(url);
      setFinishedJobs(response.data.jobs || []);
      setFinishedPagination(prev => ({ ...prev, totalCount: response.data.totalCount || 0 }));
    } catch (err) {
      console.error('Error fetching finished jobs:', err);
      setError(prev => ({ ...prev, finished: err.response?.data?.message || 'Failed to load jobs' }));
    } finally {
      setLoading(prev => ({ ...prev, finished: false }));
    }
  };
  
  const fetchAttentionJobs = async (applyFilters = false, pageNum = 0, rowsPerPageNum = 10) => {
    setLoading(prev => ({ ...prev, attention: true }));
    setError(prev => ({ ...prev, attention: null }));
    
    try {
      const queryString = buildFilterParams(pageNum, rowsPerPageNum);
      const url = `${process.env.REACT_APP_API_URL}/api/payments/attention?${queryString}`;
      
      const response = await axios.get(url);
      setAttentionJobs(response.data.jobs || []);
      setAttentionPagination(prev => ({ ...prev, totalCount: response.data.totalCount || 0 }));
    } catch (err) {
      console.error('Error fetching attention jobs:', err);
      setError(prev => ({ ...prev, attention: err.response?.data?.message || 'Failed to load jobs' }));
    } finally {
      setLoading(prev => ({ ...prev, attention: false }));
    }
  };
  
  // Action handlers
  const handleSubmitJob = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/submit/${jobId}`);
      // Update job lists
      setNewJobs(prev => prev.filter(job => job._id !== jobId));
      // Refresh current tab and submitted tab if different
      fetchNewJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPagination.page, newPagination.rowsPerPage);
      if (currentTab !== 1) {
        fetchSubmittedJobs(false, submittedPagination.page, submittedPagination.rowsPerPage);
      }
      showGlobalNotification('Job submitted successfully', 'success');
    } catch (err) {
      console.error('Error submitting job:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to submit job', 'error');
    }
  };
  
  const handleApproveJob = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/approve/${jobId}`);
      // Update job lists
      setSubmittedJobs(prev => 
        prev.map(job => job._id === jobId ? { ...job, paymentStatus: 'Approved' } : job)
      );
      showGlobalNotification('Job approved successfully', 'success');
    } catch (err) {
      console.error('Error approving job:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to approve job', 'error');
    }
  };
  
  const handleOpenRejectDialog = (jobId) => {
    setRejectDialog({
      open: true,
      jobId,
      reason: ''
    });
  };
  
  const handleCloseRejectDialog = () => {
    setRejectDialog({
      open: false,
      jobId: null,
      reason: ''
    });
  };
  
  const handleRejectJob = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/reject/${rejectDialog.jobId}`, {
        rejectionReason: rejectDialog.reason
      });
      // Update job lists
      setSubmittedJobs(prev => prev.filter(job => job._id !== rejectDialog.jobId));
      // Refresh current tab and attention tab if different
      fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), submittedPagination.page, submittedPagination.rowsPerPage);
      if (currentTab !== 3) {
        fetchAttentionJobs(false, attentionPagination.page, attentionPagination.rowsPerPage);
      }
      showGlobalNotification('Job rejected successfully', 'success');
      handleCloseRejectDialog();
    } catch (err) {
      console.error('Error rejecting job:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to reject job', 'error');
    }
  };

  // Handler for reverting a submitted job back to 'New'
  const handleRevertJob = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/revert/${jobId}`); // Use the new backend route
      // Update job lists
      setSubmittedJobs(prev => prev.filter(job => job._id !== jobId));
      // Refresh current tab and new tab if different
      fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), submittedPagination.page, submittedPagination.rowsPerPage);
      if (currentTab !== 0) {
        fetchNewJobs(false, newPagination.page, newPagination.rowsPerPage);
      }
      showGlobalNotification('Job reverted to New successfully', 'success');
    } catch (err) {
      console.error('Error reverting job:', err);
      
      // Even though there's an error, the operation might have succeeded on the backend
      // Refresh the data anyway after a short delay
      setTimeout(() => {
        fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), submittedPagination.page, submittedPagination.rowsPerPage);
        if (currentTab !== 0) {
          fetchNewJobs(false, newPagination.page, newPagination.rowsPerPage);
        }
        showGlobalNotification('Job may have been reverted. Refreshing data to check...', 'info');
      }, 1000);
    }
  };
  
  const handleOpenPaymentDialog = (jobId, currentAmount = 0) => {
    // Get start and end dates for the current week (Sunday to Saturday)
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate the Sunday (start) of the current week
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - currentDay);
    
    // Calculate the Saturday (end) of the current week
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    setPaymentDialog({
      open: true,
      jobId,
      amount: currentAmount,
      payPeriodStart: startDate,
      payPeriodEnd: endDate,
      notes: ''
    });
  };
  
  // Remove job handler
  const handleRemoveJob = async (jobId) => {
    try {
      // Assuming Org 1 users might need a specific endpoint if different logic applies
      // For now, using a generic delete endpoint, adjust if needed
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/payments/${jobId}`);
      setNewJobs(prev => prev.filter(job => job._id !== jobId));
      // Optionally show a notification
    } catch (err) {
      showGlobalNotification(err.response?.data?.message || 'Failed to remove job', 'error');
    }
  };
  const handleClosePaymentDialog = () => {
    setPaymentDialog({
      open: false,
      jobId: null,
      amount: 0,
      payPeriodStart: null,
      payPeriodEnd: null,
      notes: ''
    });
  };
  
  const handleAddPayment = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/pay/${paymentDialog.jobId}`, {
        amount: paymentDialog.amount,
        payPeriodStart: paymentDialog.payPeriodStart,
        payPeriodEnd: paymentDialog.payPeriodEnd,
        notes: paymentDialog.notes
      });
      
      // Update job lists
      setSubmittedJobs(prev => 
        prev.map(job => job._id === paymentDialog.jobId ? { ...job, paymentStatus: 'Paid' } : job)
      );
      
      showGlobalNotification('Payment added successfully', 'success');
      handleClosePaymentDialog();
    } catch (err) {
      console.error('Error adding payment:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to add payment', 'error');
    }
  };
  
  const handleMarkAsDone = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/done/${jobId}`);
      // Update job lists
      setSubmittedJobs(prev => prev.filter(job => job._id !== jobId));
      // Refresh current tab and finished tab if different
      fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), submittedPagination.page, submittedPagination.rowsPerPage);
      if (currentTab !== 2) {
        fetchFinishedJobs(false, finishedPagination.page, finishedPagination.rowsPerPage);
      }
      showGlobalNotification('Job marked as done successfully', 'success');
    } catch (err) {
      console.error('Error marking job as done:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to mark job as done', 'error');
    }
  };
  
  const handleReconsiderJob = async (jobId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/payments/reconsider/${jobId}`);
      // Update job lists
      setSubmittedJobs(prev => 
        prev.map(job => job._id === jobId ? { ...job, paymentStatus: 'Submitted' } : job)
      );
      showGlobalNotification('Job reconsidered successfully', 'success');
    } catch (err) {
      console.error('Error reconsidering job:', err);
      showGlobalNotification(err.response?.data?.message || 'Failed to reconsider job', 'error');
    }
  };
  
  const handleDownloadInvoice = (jobId) => {
    // Open the invoice in a new tab
    window.open(`${process.env.REACT_APP_API_URL}/api/payments/invoice/${jobId}`, '_blank');
  };
  
  const handleShowJobDetails = (jobId) => {
    // Toggle expanded job
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };
  
  const handleEditJob = (jobId) => {
    // Find the job in any of the job lists
    const allJobs = [...newJobs, ...submittedJobs, ...finishedJobs, ...attentionJobs];
    const job = allJobs.find(j => j._id === jobId);
    if (job) setEditJob(job);
  };
  
  // Handler for opening the edit invoice dialog
  const handleEditInvoice = (jobId) => {
    setEditInvoice({
      open: true,
      jobId
    });
  };
  
  // Handler for closing the edit invoice dialog
  const handleCloseEditInvoice = () => {
    setEditInvoice({
      open: false,
      jobId: null
    });
  };
  
  // Handler for saving invoice changes
  const handleSaveInvoice = (jobId) => {
    console.log('Invoice saved, refreshing job data for job ID:', jobId);
    
    // First, fetch the updated job to get the saved invoice data
    if (jobId) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`)
        .then(res => {
          // Update the invoice total in the invoiceTotals state
          if (res.data.invoice && res.data.invoice.total) {
            console.log('Updating invoice total from saved data:', res.data.invoice.total);
            setInvoiceTotals(prev => ({
              ...prev,
              [jobId]: res.data.invoice.total
            }));
          } else {
            // If no saved invoice data, fetch from the invoice-details endpoint
            console.log('No saved invoice data found, fetching from API');
            axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/invoice-details`)
              .then(invoiceRes => {
                setInvoiceTotals(prev => ({
                  ...prev,
                  [jobId]: invoiceRes.data?.invoice?.total ?? null
                }));
              })
              .catch(err => {
                console.error('Error fetching invoice details:', err);
              });
          }
        })
        .catch(err => {
          console.error('Error fetching updated job:', err);
        });
    }
    
    // Refresh all job lists without filters, respecting current pagination for each
    fetchSubmittedJobs(false, submittedPagination.page, submittedPagination.rowsPerPage);
    fetchNewJobs(false, newPagination.page, newPagination.rowsPerPage);
    fetchFinishedJobs(false, finishedPagination.page, finishedPagination.rowsPerPage);
    fetchAttentionJobs(false, attentionPagination.page, attentionPagination.rowsPerPage);
  };
  
  // Check if user can edit invoices
  // All users can edit EXCEPT Org 2 answering service, dispatch, and driver users
  // unless they also have the secondary role of admin
  const canEditInvoice = () => {
    if (!user) return false;
    
    // Org 1 users can always edit
    if (user.organizationLevel === 1) return true;
    
    // Org 2 users with specific roles need admin secondary role
    if (user.organizationLevel === 2) {
      const restrictedRoles = ['AS', 'DI', 'DR']; // Answering Service, Dispatch, Driver
      
      // If user has a restricted primary role
      if (user.primaryRole && restrictedRoles.includes(user.primaryRole)) {
        // Check if they have admin as secondary role
        return user.secondaryRole === 'AD'; // Admin secondary role
      }
      
      // Org 2 users with other roles can edit
      return true;
    }
    
    return false;
  };
  
  // Fetch facilities for the filter dropdown
  useEffect(() => {
    const fetchFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities`, config);
        
        // Ensure facilities is always an array
        const facilitiesData = response.data;
        if (Array.isArray(facilitiesData)) {
          setFacilities(facilitiesData);
        } else if (facilitiesData && Array.isArray(facilitiesData.records)) {
          // Handle paginated response format
          setFacilities(facilitiesData.records);
        } else {
          setFacilities([]);
        }
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([]);
      } finally {
        setLoadingFacilities(false);
      }
    };
    fetchFacilities();
  }, []);
  
  // Initialize by loading jobs for the first tab (without filters)
  useEffect(() => {
    fetchNewJobs(false, newPagination.page, newPagination.rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Initial fetch for the first tab
  
  // Job table for desktop view
  const renderJobTable = (jobs, tabName) => {
    return (
      <TableContainer component={Paper}>
        <Table aria-label={`${tabName} jobs table`}>
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Service Type</TableCell>
              <TableCell>Job Status</TableCell>
              <TableCell>Alerts</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <React.Fragment key={job._id}>
                <TableRow
                  hover
                  onClick={() => handleShowJobDetails(job._id)}
                  sx={{ cursor: 'pointer', backgroundColor: expandedJob === job._id ? 'rgba(0, 0, 0, 0.04)' : 'inherit' }}
                >
                  <TableCell>{job.po || job._id}</TableCell>
                  <TableCell>{formatDate(job.createdAt)}</TableCell>
                  <TableCell>{formatDate(job.paymentSubmittedDate) || 'N/A'}</TableCell>
                  <TableCell>{job.service}</TableCell>
                  <TableCell>{job.status}</TableCell>
                  <TableCell>
                    {job.paymentAlerts && job.paymentAlerts.length > 0 && (
                      <Tooltip
                        title={
                          <Typography variant="body2">
                            {job.paymentRejectionReason ||
                             (job.paymentAlerts.find(alert => alert.type === 'rejection')?.message) ||
                             'Invoice was rejected'}
                          </Typography>
                        }
                        arrow
                      >
                        <IconButton color="warning" size="small">
                          <WarningIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoiceTotals[job._id] !== undefined && invoiceTotals[job._id] !== null
                      ? `$${invoiceTotals[job._id].toFixed(2)}`
                      : <span style={{ color: '#aaa' }}>Loading...</span>
                    }
                  </TableCell>
                  <TableCell>
                    {renderStatusCell(job, tabName)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* Disable edit button for jobs in the Done tab */}
                      {tabName !== 'finished' ? (
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJob(job._id);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="primary"
                          size="small"
                          disabled
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadInvoice(job._id);
                        }}
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                      {/* Add Edit Invoice button */}
                      {canEditInvoice() && (
                        <IconButton
                          color="info"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditInvoice(job._id);
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>INV</Typography>
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
                {/* Expanded job details row */}
                {expandedJob === job._id && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      {/* Use JobDetails for expanded job details */}
                      <JobDetails job={job} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Job cards for mobile view
  const renderJobCards = (jobs, tabName) => {
    return (
      <Stack spacing={2}>
        {jobs.map((job) => (
          <Card key={job._id}>
            <CardContent>
              <Typography variant="h6" component="div">
                Job ID: {job.po || job._id}
              </Typography>
              <Typography color="text.secondary">
                {job.service} - {job.status}
              </Typography>
              <Typography variant="body2">
                Created: {formatDate(job.createdAt)}
              </Typography>
              <Typography variant="body2">
                {job.customerName} - {job.vehicle?.year} {job.vehicle?.make} {job.vehicle?.model}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Balance: {invoiceTotals[job._id] !== undefined && invoiceTotals[job._id] !== null
                  ? `$${invoiceTotals[job._id].toFixed(2)}`
                  : <span style={{ color: '#aaa' }}>Loading...</span>
                }
              </Typography>
              {job.paymentAlerts && job.paymentAlerts.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="warning.main">
                    {job.paymentRejectionReason ||
                     (job.paymentAlerts.find(alert => alert.type === 'rejection')?.message) ||
                     'Invoice was rejected'}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                {renderStatusCell(job, tabName)}
              </Box>
            </CardContent>
            <CardActions>
              {/* Disable edit button for jobs in the Done tab */}
              {tabName !== 'finished' ? (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditJob(job._id)}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  disabled
                >
                  Edit
                </Button>
              )}
              {/* Add Edit Invoice button for mobile */}
              {canEditInvoice() && (
                <Button
                  size="small"
                  color="info"
                  onClick={() => handleEditInvoice(job._id)}
                >
                  Edit Invoice
                </Button>
              )}
              <Button 
                size="small" 
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handleDownloadInvoice(job._id)}
              >
                Invoice
              </Button>
              <Button 
                size="small"
                onClick={() => handleShowJobDetails(job._id)}
              >
                {expandedJob === job._id ? "Hide Details" : "Show Details"}
              </Button>
            </CardActions>
            {expandedJob === job._id && (
              <CardContent>
                {/* Use JobDetails for expanded job details (mobile) */}
                <JobDetails job={job} />
              </CardContent>
            )}
          </Card>
        ))}
      </Stack>
    );
  };
  
  // Render status cell based on tab and permissions
  const renderStatusCell = (job, tabName) => {
    const isOrg1User = user?.organizationLevel === 1;
    const isOrg2User = user?.organizationLevel === 2;
    
    // Determine the organization level of the assigned entity
    // Assumes driverId is populated with at least organizationLevel for Org 1 jobs
    const assignedDriverOrgLevel = (job.driverId && typeof job.driverId === 'object') ? job.driverId.organizationLevel : null;
    const isOrg1JobAssignment = assignedDriverOrgLevel === 1;
    
    // Check if this is an Org 2 job - either via assignedVendorNumber OR driver's vendor identity
    const isOrg2JobAssignment =
      // Primary check: job has an assignedVendorNumber
      !!job.assignedVendorNumber ||
      // Fallback check: driver exists with vendor information (organizationLevel = 2)
      (job.driverId && typeof job.driverId === 'object' && assignedDriverOrgLevel === 2);

    // New tab logic
    if (tabName === 'new') {
      // Case 1: Current user is Org 1
      if (isOrg1User) {
        // Subcase 1.1: Job is assigned to an Org 1 driver
        if (isOrg1JobAssignment) {
          return (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" size="small" onClick={(e) => { e.stopPropagation(); handleSubmitJob(job._id); }}>
                Submit
              </Button>
              <Button variant="outlined" color="error" size="small" onClick={(e) => { e.stopPropagation(); handleRemoveJob(job._id); }}>
                Remove
              </Button>
            </Stack>
          );
        } 
        // Subcase 1.2: Job is assigned to an Org 2 vendor (or potentially unassigned)
        else { 
           // Org 1 can remove jobs assigned to Org 2 vendors or unassigned jobs
           return (
             <Button variant="outlined" color="error" size="small" onClick={(e) => { e.stopPropagation(); handleRemoveJob(job._id); }}>
               Remove
             </Button>
           );
        }
      } 
      // Case 2: Current user is Org 2
      else if (isOrg2User) {
        // Subcase 2.1: Job assigned to this user's vendor number
        // Use case-insensitive comparison to match backend logic
        const vendorMatches =
          // Primary check: assignedVendorNumber matches user's vendorNumber
          (job.assignedVendorNumber && user?.vendorNumber &&
           job.assignedVendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
          // Fallback check: driver's vendor info matches user's vendor info
          (job.driverId && typeof job.driverId === 'object' &&
           ((job.driverId.vendorNumber && user?.vendorNumber &&
             job.driverId.vendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
            (job.driverId.vendorId && user?.vendorId &&
             job.driverId.vendorId.toLowerCase() === user.vendorId.toLowerCase())));
             
        // DEBUG: Log vendor matching for Org 2 users
        if (isOrg2User) {
          console.log(`DEBUG [renderStatusCell] Org 2 user checking New job: isOrg2JobAssignment=${isOrg2JobAssignment}, vendorMatches=${vendorMatches}, job.assignedVendorNumber=${job.assignedVendorNumber}, user.vendorNumber=${user?.vendorNumber}, user.vendorId=${user?.vendorId}`);
          if (job.driverId && typeof job.driverId === 'object') {
            console.log(`DEBUG [renderStatusCell] Driver info: vendorNumber=${job.driverId.vendorNumber}, vendorId=${job.driverId.vendorId}`);
          }
        }
        
        if (isOrg2JobAssignment && user && vendorMatches) {
          return (
            <Button variant="contained" color="primary" size="small" onClick={(e) => { e.stopPropagation(); handleSubmitJob(job._id); }}>
              Submit
            </Button>
          );
        }
        // Subcase 2.2: Job assigned to Org 1 or another Org 2 vendor
        else {
          return null; // Org 2 users see no actions for jobs not assigned to them
        }
      }
      // Default: No actions for other users
      else {
        return null;
      }
    }
    
    // Submitted tab logic
    if (tabName === 'submitted') {
      const isJobAssignedToThisOrg2Vendor = isOrg2User && isOrg2JobAssignment && user && (
        // Primary check: assignedVendorNumber matches user's vendorNumber
        (job.assignedVendorNumber && user.vendorNumber &&
         job.assignedVendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
        // Fallback check: driver's vendor info matches user's vendor info
        (job.driverId && typeof job.driverId === 'object' &&
         ((job.driverId.vendorNumber && user.vendorNumber &&
           job.driverId.vendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
          (job.driverId.vendorId && user.vendorId &&
           job.driverId.vendorId.toLowerCase() === user.vendorId.toLowerCase())))
      );
      const isOrg1DriverActingAsSP = isOrg1User && isOrg1JobAssignment; // Org 1 user viewing their own Org 1 job

      if (job.paymentStatus === 'Submitted') {
        // DEBUG: Log permission check for Org 1 users
        if (isOrg1User) {
          console.log(`DEBUG [renderStatusCell] Org 1 user checking permissions: canApprovePayment=${hasPermission('canApprovePayment')}, user.organizationLevel=${user?.organizationLevel}`);
        }
        
        // Org 1 users see Approve/Reject/Revert - removed permission check to show buttons for all Org 1 users
        if (isOrg1User) {
          return (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="success" size="small" onClick={(e) => { e.stopPropagation(); handleApproveJob(job._id); }}>
                Approve
              </Button>
              <Button variant="contained" color="error" size="small" onClick={(e) => { e.stopPropagation(); handleOpenRejectDialog(job._id); }}>
                Reject
              </Button>
              <Button variant="outlined" color="secondary" size="small" startIcon={<ReplayIcon />} onClick={(e) => { e.stopPropagation(); handleRevertJob(job._id); }}>
                Revert
              </Button>
            </Stack>
          );
        }
        // Org 2 users (or Org 1 acting as SP) see Revert only if it's their job
        else if (isJobAssignedToThisOrg2Vendor || isOrg1DriverActingAsSP) {
           return (
             <Button variant="outlined" color="secondary" size="small" startIcon={<ReplayIcon />} onClick={(e) => { e.stopPropagation(); handleRevertJob(job._id); }}>
               Revert
             </Button>
           );
        }
        // Otherwise, show pending chip or nothing
        else if (isOrg1User) { // Org 1 viewing an Org 2 job they can't approve
             return <Chip label="Pending" color="warning" size="small" />;
        } else {
             return null;
        }
      }
      
      if (job.paymentStatus === 'Approved') {
        // DEBUG: Log permission check for Org 1 users
        if (isOrg1User) {
          console.log(`DEBUG [renderStatusCell] Org 1 user checking permissions for Approved job: canProcessPayment=${hasPermission('canProcessPayment')}, user.organizationLevel=${user?.organizationLevel}`);
        }
        
        if (isOrg1User) { // Removed permission check to show buttons for all Org 1 users
          return (
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" size="small" onClick={(e) => { e.stopPropagation(); handleOpenPaymentDialog(job._id, job.paymentAmount || job.payment || 0); }}>
                Add Pay
              </Button>
              <Button variant="outlined" color="primary" size="small" onClick={(e) => { e.stopPropagation(); handleReconsiderJob(job._id); }}>
                Reconsider
              </Button>
            </Stack>
          );
        }
         // Org 2 user (or Org 1 acting as SP) sees "Approved" chip if it's their job
        else if (isJobAssignedToThisOrg2Vendor || isOrg1DriverActingAsSP) {
           return <Chip label="Approved" color="success" size="small" />;
        } else {
           return null;
        }
      }
      
      if (job.paymentStatus === 'Paid') {
        // DEBUG: Log permission check for Org 1 users
        if (isOrg1User) {
          console.log(`DEBUG [renderStatusCell] Org 1 user checking permissions for Paid job: canProcessPayment=${hasPermission('canProcessPayment')}, user.organizationLevel=${user?.organizationLevel}`);
        }
        
        if (isOrg1User) { // Removed permission check to show buttons for all Org 1 users
          return (
            <Button variant="contained" color="success" size="small" onClick={(e) => { e.stopPropagation(); handleMarkAsDone(job._id); }}>
              Done
            </Button>
          );
        }
         // Org 1 or relevant Org 2 user sees "Paid" chip
         return (isOrg1User || (isOrg2JobAssignment && user && (
                               // Primary check: assignedVendorNumber matches
                               (job.assignedVendorNumber && user.vendorNumber &&
                                job.assignedVendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
                               // Fallback check: driver's vendor info matches
                               (job.driverId && typeof job.driverId === 'object' &&
                                ((job.driverId.vendorNumber && user.vendorNumber &&
                                  job.driverId.vendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
                                 (job.driverId.vendorId && user.vendorId &&
                                  job.driverId.vendorId.toLowerCase() === user.vendorId.toLowerCase()))))))
              ? <Chip label="Paid" color="success" size="small" />
               : null;
      }
      
      // Fallback for Submitted tab if none of the above match
      return null; 
    }
    
    // Finished tab logic
    if (tabName === 'finished') {
       // Show "Closed" chip only if the user is Org 1 or it's their Org 2 job
       if (isOrg1User || (isOrg2JobAssignment && user && (
                           // Primary check: assignedVendorNumber matches
                           (job.assignedVendorNumber && user.vendorNumber &&
                            job.assignedVendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
                           // Fallback check: driver's vendor info matches
                           (job.driverId && typeof job.driverId === 'object' &&
                            ((job.driverId.vendorNumber && user.vendorNumber &&
                              job.driverId.vendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
                             (job.driverId.vendorId && user.vendorId &&
                              job.driverId.vendorId.toLowerCase() === user.vendorId.toLowerCase())))))) {
         return <Chip label="Closed" color="success" size="small" />;
       }
       return null;
    }
    
    // Attention tab logic
    if (tabName === 'attention') {
      const isAssignedToThisOrg2VendorForAttention = isOrg2User && isOrg2JobAssignment && user && (
        // Primary check: assignedVendorNumber matches
        (job.assignedVendorNumber && user.vendorNumber &&
         job.assignedVendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
        // Fallback check: driver's vendor info matches
        (job.driverId && typeof job.driverId === 'object' &&
         ((job.driverId.vendorNumber && user.vendorNumber &&
           job.driverId.vendorNumber.toLowerCase() === user.vendorNumber.toLowerCase()) ||
          (job.driverId.vendorId && user.vendorId &&
           job.driverId.vendorId.toLowerCase() === user.vendorId.toLowerCase())))
      );
      
      // DEBUG: Log permission check for Org 2 users
      if (isOrg2User) {
        console.log(`DEBUG [renderStatusCell] Org 2 user checking permissions for Attention job: canSubmitForPayment=${hasPermission('canSubmitForPayment')}, isAssignedToThisOrg2VendorForAttention=${isAssignedToThisOrg2VendorForAttention}`);
      }
      
      if (isAssignedToThisOrg2VendorForAttention) { // Removed permission check to show button for all relevant Org 2 users
        return (
          <Button variant="contained" color="warning" size="small" onClick={(e) => { e.stopPropagation(); handleSubmitJob(job._id); }}>
            Resubmit
          </Button>
        );
      }
      // Show Rejected chip for Org 1 or Org 2 users if it's relevant to them
      if (isOrg1User || isAssignedToThisOrg2VendorForAttention) {
         return <Chip label="Rejected" color="error" size="small" />;
      }
       return null;
    }
    
    // Default fallback for any other tab/status
    return null; 
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Side Menu */}
      <SideMenu />
      
      {/* Main Content */}
      <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}> {/* Added padding */}
      
      {/* Filter Section */}
      <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }} data-tour="payments-filters">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
          <Typography variant="h6">Filters</Typography>
          <Button
            startIcon={<FilterListIcon />}
            onClick={toggleFilterVisibility}
            aria-expanded={showFilters}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>
        {showFilters && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="facility-filter-label">Facility</InputLabel>
                  <Select
                    labelId="facility-filter-label"
                    label="Facility"
                    name="facilityId"
                    value={filters.facilityId}
                    onChange={handleFilterChange}
                    disabled={loadingFacilities}
                  >
                    <MenuItem value=""><em>All Facilities</em></MenuItem>
                    {loadingFacilities ? (
                      <MenuItem value="" disabled><CircularProgress size={20} /></MenuItem>
                    ) : (
                      Array.isArray(facilities) && facilities.map((facility) => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {facility.facilityName} ({facility.vendorNumber || 'N/A'})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  value={filters.city}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  name="state"
                  value={filters.state}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Zip Code"
                  name="zip"
                  value={filters.zip}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Service"
                  name="service"
                  value={filters.service}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer Name"
                  name="customerName"
                  value={filters.customerName}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer Phone"
                  name="customerPhone"
                  value={filters.customerPhone}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="PO Number"
                  name="poNumber"
                  value={filters.poNumber}
                  onChange={handleFilterChange}
                />
              </Grid>
            </Grid>
            
            {/* Filter Action Buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                size="small"
              >
                Search
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            </Box>
          </>
        )}
      </Box>
      
      <Box sx={{ mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          label="Search invoices"
          placeholder="PO, driver, account, customer, service, city…"
          value={paymentSearch}
          onChange={(e) => setPaymentSearch(e.target.value)}
          data-tour="payments-search"
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="payment tabs"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          data-tour="payments-tabs"
        >
          <Tab label="New" data-tour="payments-tab-new" />
          <Tab label="Submitted" data-tour="payments-tab-submitted" />
          <Tab label="Done" data-tour="payments-tab-done" />
          <Tab label="Attention Needed" data-tour="payments-tab-attention" />
          <Tab label="Payment Records" data-tour="payments-tab-records" />
        </Tabs>
      </Box>

      {/* Payment Records tab (weekly ACH payouts + CSV export) */}
      <TabPanel value={currentTab} index={4}>
        <Box data-tour="payments-records"><PayoutRecordsPanel user={user} /></Box>
      </TabPanel>

      {/* New tab */}
      <TabPanel value={currentTab} index={0}>
        <Typography variant="h5" gutterBottom>
          New Invoices
        </Typography>
        {loading.new ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error.new ? (
          <Alert severity="error">{error.new}</Alert>
        ) : newJobs.length === 0 ? (
          <Alert severity="info">No new jobs found</Alert>
        ) : (
          <>
            {isMobile ? renderJobCards(filterJobsBySearch(newJobs), 'new') : renderJobTable(filterJobsBySearch(newJobs), 'new')}
            <TablePagination
              component="div"
              count={newPagination.totalCount}
              page={newPagination.page}
              onPageChange={(event, newPageVal) => {
                setNewPagination(prev => ({ ...prev, page: newPageVal }));
                fetchNewJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPageVal, newPagination.rowsPerPage);
              }}
              rowsPerPage={newPagination.rowsPerPage}
              onRowsPerPageChange={(event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setNewPagination(prev => ({ ...prev, page: 0, rowsPerPage: newRowsPerPage }));
                fetchNewJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), 0, newRowsPerPage);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
      </TabPanel>
      
      {/* Submitted tab */}
      <TabPanel value={currentTab} index={1}>
        <Typography variant="h5" gutterBottom>
          Submitted Invoices
        </Typography>
        {loading.submitted ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error.submitted ? (
          <Alert severity="error">{error.submitted}</Alert>
        ) : submittedJobs.length === 0 ? (
          <Alert severity="info">No submitted jobs found</Alert>
        ) : (
          <>
            {isMobile ? renderJobCards(filterJobsBySearch(submittedJobs), 'submitted') : renderJobTable(filterJobsBySearch(submittedJobs), 'submitted')}
            <TablePagination
              component="div"
              count={submittedPagination.totalCount}
              page={submittedPagination.page}
              onPageChange={(event, newPageVal) => {
                setSubmittedPagination(prev => ({ ...prev, page: newPageVal }));
                fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPageVal, submittedPagination.rowsPerPage);
              }}
              rowsPerPage={submittedPagination.rowsPerPage}
              onRowsPerPageChange={(event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setSubmittedPagination(prev => ({ ...prev, page: 0, rowsPerPage: newRowsPerPage }));
                fetchSubmittedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), 0, newRowsPerPage);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
      </TabPanel>
      
      {/* Finished tab */}
      <TabPanel value={currentTab} index={2}>
        <Typography variant="h5" gutterBottom>
          Done Invoices
        </Typography>
        {loading.finished ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error.finished ? (
          <Alert severity="error">{error.finished}</Alert>
        ) : finishedJobs.length === 0 ? (
          <Alert severity="info">No completed jobs found</Alert>
        ) : (
          <>
            {isMobile ? renderJobCards(filterJobsBySearch(finishedJobs), 'finished') : renderJobTable(filterJobsBySearch(finishedJobs), 'finished')}
            <TablePagination
              component="div"
              count={finishedPagination.totalCount}
              page={finishedPagination.page}
              onPageChange={(event, newPageVal) => {
                setFinishedPagination(prev => ({ ...prev, page: newPageVal }));
                fetchFinishedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPageVal, finishedPagination.rowsPerPage);
              }}
              rowsPerPage={finishedPagination.rowsPerPage}
              onRowsPerPageChange={(event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setFinishedPagination(prev => ({ ...prev, page: 0, rowsPerPage: newRowsPerPage }));
                fetchFinishedJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), 0, newRowsPerPage);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
      </TabPanel>
      
      {/* Attention tab */}
      <TabPanel value={currentTab} index={3}>
        <Typography variant="h5" gutterBottom>
          Invoices Needing Attention
        </Typography>
        {loading.attention ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error.attention ? (
          <Alert severity="error">{error.attention}</Alert>
        ) : attentionJobs.length === 0 ? (
          <Alert severity="info">No jobs needing attention</Alert>
        ) : (
          <>
            {isMobile ? renderJobCards(filterJobsBySearch(attentionJobs), 'attention') : renderJobTable(filterJobsBySearch(attentionJobs), 'attention')}
            <TablePagination
              component="div"
              count={attentionPagination.totalCount}
              page={attentionPagination.page}
              onPageChange={(event, newPageVal) => {
                setAttentionPagination(prev => ({ ...prev, page: newPageVal }));
                fetchAttentionJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), newPageVal, attentionPagination.rowsPerPage);
              }}
              rowsPerPage={attentionPagination.rowsPerPage}
              onRowsPerPageChange={(event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                setAttentionPagination(prev => ({ ...prev, page: 0, rowsPerPage: newRowsPerPage }));
                fetchAttentionJobs(!!(filters.facilityId || filters.city || filters.state || filters.zip || filters.service || filters.customerName || filters.customerPhone || filters.poNumber), 0, newRowsPerPage);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
      </TabPanel>
      
      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={handleCloseRejectDialog}
        aria-labelledby="reject-dialog-title"
      >
        <DialogTitle id="reject-dialog-title">Reject Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this invoice:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog(prev => ({ ...prev, reason: e.target.value }))}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleRejectJob} 
            color="error" 
            disabled={!rejectDialog.reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Payment Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog
          open={paymentDialog.open}
          onClose={handleClosePaymentDialog}
          aria-labelledby="payment-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="payment-dialog-title">Process Payment</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter payment details:
            </DialogContentText>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  id="amount"
                  label="Payment Amount"
                  type="number"
                  fullWidth
                  value={paymentDialog.amount}
                  onChange={(e) => setPaymentDialog(prev => ({ ...prev, amount: e.target.value }))}
                  InputProps={{
                    startAdornment: <Typography>$</Typography>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Pay Period Start"
                  value={paymentDialog.payPeriodStart}
                  onChange={(date) => setPaymentDialog(prev => ({ ...prev, payPeriodStart: date }))}
                  slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Pay Period End"
                  value={paymentDialog.payPeriodEnd}
                  onChange={(date) => setPaymentDialog(prev => ({ ...prev, payPeriodEnd: date }))}
                  slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  id="notes"
                  label="Notes"
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  value={paymentDialog.notes}
                  onChange={(e) => setPaymentDialog(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaymentDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleAddPayment} 
              color="primary" 
              variant="contained"
              disabled={!paymentDialog.amount || paymentDialog.amount <= 0 || !paymentDialog.payPeriodStart || !paymentDialog.payPeriodEnd}
            >
              Process Payment
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
      {/* Edit Job Dialog */}
      {editJob && (
        <JobDialog
          open={true}
          onClose={() => setEditJob(null)}
          isEditing={true}
          selectedJob={editJob}
          vehicleData={vehicleData}
        />
      )}
      
      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={editInvoice.open}
        onClose={handleCloseEditInvoice}
        jobId={editInvoice.jobId}
        onSave={handleSaveInvoice}
      />
      </Box> {/* Close Main Content Box */}
    </Box>
  );
}; // End of Payments component function

export default Payments; // Export statement outside the function
